import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';
import { CurrencyIcon } from '../../../components/icons';

/** `null` = chưa có baseline thật để so sánh -> ẩn badge, không hiện 0%. */
export interface DashboardStats {
  totalRevenue: number;
  revenueChange: number | null;
  totalOrders: number;
  ordersChange: number | null;
  totalTransactions: number;
  transactionsChange: number | null;
  todayTransactions: number;
  todayTransactionsChange: number | null;
}

interface DashboardStatsRowProps {
  stats: DashboardStats;
  isLoading?: boolean;
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

// ---- Trend ----

function TrendBadge({ change, baselineLabel }: { change: number | null; baselineLabel: string }) {
  // Không có baseline thật thì không vẽ badge — hiện "+0%" là bịa số.
  if (change === null) return null;

  const isPositive = change >= 0;
  return (
    <div className="flex items-center gap-1">
      <span className={cn('inline-flex items-center gap-0.5 text-xs font-bold', isPositive ? 'text-emerald-600' : 'text-red-500')}>
        {isPositive ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9.5 3.5H13.5V7.5" stroke="#16A34A" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M13.5 3.5L7.85 9.15C7.75654 9.24161 7.63088 9.29293 7.5 9.29293C7.36912 9.29293 7.24346 9.24161 7.15 9.15L4.85 6.85C4.75654 6.75839 4.63088 6.70707 4.5 6.70707C4.36912 6.70707 4.24346 6.75839 4.15 6.85L0.5 10.5" stroke="#16A34A" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9.5 10.5H13.5V6.5" stroke="#BA1A1A" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M13.5 10.5L7.85 4.85C7.75654 4.75839 7.63088 4.70707 7.5 4.70707C7.36912 4.70707 7.24346 4.75839 7.15 4.85L4.85 7.15C4.75654 7.24161 4.63088 7.29293 4.5 7.29293C4.36912 7.29293 4.24346 7.24161 4.15 7.15L0.5 3.5" stroke="#BA1A1A" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        )}
        {isPositive ? '+' : ''}{change}%
      </span>
      <span className="text-xs font-medium text-slate-700">{baselineLabel}</span>
    </div>
  );
}

// ---- Card ----

interface StatCardProps {
  label: string;
  value: string;
  change: number | null;
  baselineLabel: string;
  iconBg: string;
  icon: React.ReactNode;
}

function StatCard({ label, value, change, baselineLabel, iconBg, icon }: StatCardProps) {
  return (
    <div
      className="flex flex-col gap-1.5 rounded-xl border border-slate-100 bg-white p-3"
      style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}
    >
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-xl', iconBg)}>
          {icon}
        </div>
      </div>
      <p className="text-xl font-bold leading-8 text-[#014361] sm:text-[32px] sm:leading-[40px]">{value}</p>
      <TrendBadge change={change} baselineLabel={baselineLabel} />
    </div>
  );
}

// ---- Main ----

export function DashboardStatsRow({ stats, isLoading }: DashboardStatsRowProps) {
  const { t } = useI18n();
  const money = useMoney();

  // Helper nằm trong component vì `useMoney()` là hook.
  const formatRevenue = (value: number): string =>
    money.format(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-[130px] animate-pulse rounded-xl bg-slate-200" />
        ))}
      </div>
    );
  }

  // Thẻ theo ngày so với hôm qua; thẻ theo khoảng ngày so với kỳ liền trước.
  const vsYesterday = t('vsYesterday');
  const vsPreviousPeriod = t('vsPreviousPeriod');

  const cards: StatCardProps[] = [
    {
      label: t('todaysRevenue'),
      value: formatRevenue(stats.totalRevenue),
      change: stats.revenueChange,
      baselineLabel: vsYesterday,
      iconBg: 'bg-sky-50',
      // Bug #30: icon phải là ký hiệu tiền của app (₴), không phải "$" vẽ cứng.
      icon: <CurrencyIcon size={30} style={{ color: '#0288D1' }} />,
    },
    {
      label: t('totalOrders'),
      value: formatNumber(stats.totalOrders),
      change: stats.ordersChange,
      baselineLabel: vsPreviousPeriod,
      iconBg: 'bg-emerald-50',
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path d="M20.7407 5.33594C23.3184 5.35194 24.7146 5.48127 25.6248 6.50527C26.6666 7.67727 26.6666 9.5626 26.6666 13.3333V21.3333C26.6666 25.1053 26.6666 26.9906 25.6248 28.1626C24.5842 29.3333 22.9072 29.3333 19.5555 29.3333H12.4444C9.09266 29.3333 7.41562 29.3333 6.37503 28.1626C5.33325 26.9893 5.33325 25.1053 5.33325 21.3333V13.3333C5.33325 9.5626 5.33325 7.67727 6.37503 6.50527C7.28525 5.48127 8.6814 5.35194 11.2592 5.33594" stroke="#219653" stroke-width="1.5" />
        <path d="M11.2592 18.6667H20.7406M10.074 14H21.9258M12.4443 23.3333H19.5555" stroke="#219653" stroke-width="1.5" stroke-linecap="round" />
        <path d="M11.2593 4.66797C11.2593 4.13754 11.4466 3.62883 11.78 3.25376C12.1134 2.87868 12.5656 2.66797 13.0371 2.66797H18.963C19.4345 2.66797 19.8867 2.87868 20.2201 3.25376C20.5535 3.62883 20.7408 4.13754 20.7408 4.66797V6.0013C20.7408 6.53174 20.5535 7.04044 20.2201 7.41552C19.8867 7.79059 19.4345 8.0013 18.963 8.0013H13.0371C12.5656 8.0013 12.1134 7.79059 11.78 7.41552C11.4466 7.04044 11.2593 6.53174 11.2593 6.0013V4.66797Z" stroke="#219653" stroke-width="1.5" />
      </svg>,
    },
    {
      label: t('totalTransactions'),
      value: formatRevenue(stats.totalTransactions),
      change: stats.transactionsChange,
      baselineLabel: vsPreviousPeriod,
      iconBg: 'bg-purple-50',
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path d="M7.99992 27.9997C7.24792 27.9997 6.61547 27.7433 6.10259 27.2304C5.5897 26.7175 5.33325 26.0851 5.33325 25.3331V22.6664H9.33325V4.51305L11.0266 5.69172L12.7693 4.51172L14.5133 5.69172L16.2559 4.51172L17.9999 5.69172L19.7439 4.51305L21.4879 5.69172L23.2306 4.51305L24.9746 5.69172L26.6666 4.51172V25.3331C26.6666 26.0851 26.4101 26.7175 25.8973 27.2304C25.3844 27.7433 24.7519 27.9997 23.9999 27.9997H7.99992ZM23.9999 26.6664C24.3777 26.6664 24.6946 26.5384 24.9506 26.2824C25.2066 26.0264 25.3341 25.7099 25.3333 25.3331V6.66639H10.6666V22.6664H22.6666V25.3331C22.6666 25.7108 22.7946 26.0277 23.0506 26.2837C23.3066 26.5397 23.623 26.6673 23.9999 26.6664ZM12.5133 11.3331V9.99972H19.6413V11.3331H12.5133ZM12.5133 15.3331V13.9997H19.6413V15.3331H12.5133ZM22.5133 11.6931C22.2377 11.6931 21.9977 11.5908 21.7933 11.3864C21.5888 11.1819 21.4866 10.9419 21.4866 10.6664C21.4866 10.3908 21.5888 10.1508 21.7933 9.94639C21.9977 9.74194 22.2377 9.63972 22.5133 9.63972C22.7888 9.63972 23.0284 9.74194 23.2319 9.94639C23.4355 10.1508 23.5377 10.3908 23.5386 10.6664C23.5395 10.9419 23.4373 11.1819 23.2319 11.3864C23.0266 11.5908 22.7879 11.6931 22.5133 11.6931ZM22.5119 15.6931C22.2373 15.6931 21.9977 15.5908 21.7933 15.3864C21.5888 15.1819 21.4866 14.9419 21.4866 14.6664C21.4866 14.3908 21.5888 14.1508 21.7933 13.9464C21.9977 13.7419 22.2377 13.6397 22.5133 13.6397C22.7888 13.6397 23.0284 13.7419 23.2319 13.9464C23.4355 14.1508 23.5377 14.3908 23.5386 14.6664C23.5395 14.9419 23.4373 15.1819 23.2319 15.3864C23.0266 15.5908 22.7866 15.6931 22.5119 15.6931ZM7.99992 26.6664H21.3333V23.9997H6.66659V25.3331C6.66659 25.7108 6.79459 26.0277 7.05059 26.2837C7.30659 26.5397 7.62303 26.6673 7.99992 26.6664Z" fill="#8A38F5" />
      </svg>,
    },
    {
      label: t('todaysTransactions'),
      value: formatNumber(stats.todayTransactions),
      change: stats.todayTransactionsChange,
      baselineLabel: vsYesterday,
      iconBg: 'bg-amber-50',
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path d="M25.125 10.5C24.9419 13.0419 23.0562 15 21 15C18.9437 15 17.055 13.0425 16.875 10.5C16.6875 7.85562 18.5231 6 21 6C23.4769 6 25.3125 7.90375 25.125 10.5Z" stroke="#F2994A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M21 19C16.9269 19 13.01 21.0231 12.0287 24.9631C11.8987 25.4844 12.2256 26 12.7612 26H29.2394C29.775 26 30.1 25.4844 29.9719 24.9631C28.9906 20.96 25.0737 19 21 19Z" stroke="#F2994A" stroke-width="1.5" stroke-miterlimit="10" />
        <path d="M12.5001 11.6213C12.3539 13.6513 10.8301 15.25 9.18761 15.25C7.54511 15.25 6.01886 13.6519 5.87511 11.6213C5.72573 9.50938 7.20886 8 9.18761 8C11.1664 8 12.6495 9.54813 12.5001 11.6213Z" stroke="#F2994A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M12.8749 19.1258C11.7468 18.6089 10.5043 18.4102 9.18738 18.4102C5.93738 18.4102 2.80613 20.0258 2.02175 23.1727C1.91863 23.5889 2.17988 24.0008 2.60738 24.0008H9.62488" stroke="#F2994A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" />
      </svg>,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card, index) => (
        <StatCard key={index} {...card} />
      ))}
    </div>
  );
}
